import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { auth } from '../firebase';

const SettingsModal = ({ type, onClose, userToken, userLevel = "Basic" }) => {
  const [formData, setFormData] = useState({
    currentPassword: '',
    primaryValue: '', 
    confirmValue: '',
  });
  const [status, setStatus] = useState({ loading: false, error: '', success: '' });

  const isEmail = type === 'EMAIL';

  const handleUpgradeLevel = async () => {
    setStatus({ loading: true, error: '', success: '' });
    try {
      const user = auth.currentUser;
      if (!user) throw new Error("No authenticated user found.");
      
      const freshToken = await user.getIdToken(true);
      
      const response = await fetch('https://us-central1-stockmarket-83a73.cloudfunctions.net/purchaseGoldShopItem', {
        method: 'POST',
        mode: 'cors',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${freshToken}`
        },
        body: JSON.stringify({ itemId: 'preferred_account_upgrade' })
      });

      const data = await response.json();

      if (data.success) {
        setStatus({ loading: false, error: '', success: "ACCOUNT UPGRADED TO PREFERRED" });
        setTimeout(onClose, 2000);
      } else {
        setStatus({ loading: false, error: (data.message || "UPGRADE REJECTED").toUpperCase(), success: '' });
      }
    } catch (err) {
      console.error("Upgrade Error:", err);
      setStatus({ loading: false, error: "TRANSACTION ERROR: CHECK GOLD BALANCE", success: '' });
    }
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    setStatus({ loading: true, error: '', success: '' });

    if (formData.primaryValue !== formData.confirmValue) {
      return setStatus({ loading: false, error: "INPUT MISMATCH: VALUES DO NOT MATCH", success: '' });
    }

    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error("No authenticated user found.");
      }
      
      const freshToken = await user.getIdToken(true);

      const endpoint = isEmail 
        ? 'https://changeemail-5dhfwyjvzq-uc.a.run.app' 
        : 'https://changepassword-5dhfwyjvzq-uc.a.run.app';

      const response = await fetch(endpoint, {
        method: 'POST',
        mode: 'cors',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${freshToken}`
        },
        body: JSON.stringify({
          currentPassword: formData.currentPassword,
          [isEmail ? 'newEmail' : 'newPassword']: formData.primaryValue
        })
      });

      const data = await response.json();

      if (data.success) {
        setStatus({ loading: false, error: '', success: "UPDATE SUCCESS" });
        setTimeout(onClose, 2000);
      } else {
        setStatus({ loading: false, error: (data.message || "TRANSACTION REFUSED").toUpperCase(), success: '' });
      }
    } catch (err) {
      console.error("Auth Error:", err);
      const errorMsg = err.message === "No authenticated user found." 
        ? "SESSION EXPIRED: RE-LOGIN REQUIRED" 
        : "CONNECTION FAILURE: CLOUD RUN REJECTED";
        
      setStatus({ loading: false, error: errorMsg, success: '' });
    }
  };

  // Safe checks: if undefined, or equals "basic" regardless of casing
  const isBasicLevel = !userLevel || userLevel.toString().toLowerCase() === "basic";

  return createPortal(
    <div className="modal-overlay" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 10000 }}>
      <div className="floating-modal-container">

        <div className="draggable-title">
          <span>UPDATE {type}</span>
          <button onClick={onClose}>&times;</button>
        </div>

        <div className="scrollable-content">
          
          {/* UPGRADE SECTION BLOCK */}
          {isBasicLevel && (
            <div style={{ border: '1px dashed #FFD700', padding: '12px', marginBottom: '16px', background: 'rgba(255, 215, 0, 0.02)', fontFamily: "'JetBrains Mono', monospace" }}>
              <div style={{ fontSize: '10px', color: '#FFD700', fontWeight: 'bold', marginBottom: '4px' }}>ACCOUNT LEVEL: BASIC</div>
              <div style={{ fontSize: '9px', color: '#888', marginBottom: '10px', lineHeight: '1.3' }}>
                Unlock lower transaction slippage multipliers and margin trading perks.
              </div>
              <button 
                type="button" 
                onClick={handleUpgradeLevel}
                disabled={status.loading}
                style={{
                  width: '100%',
                  background: 'linear-gradient(90deg, #FFD700, #FFA500)',
                  border: 'none',
                  color: '#000',
                  fontSize: '10px',
                  fontWeight: 'bold',
                  padding: '8px',
                  cursor: 'pointer',
                  letterSpacing: '0.5px'
                }}
              >
                {status.loading ? "CONTACTING VAULT..." : "UPGRADE TRADER LEVEL (1500 G)"}
              </button>
            </div>
          )}

          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <label style={{ fontSize: '10px', color: 'rgba(80, 227, 194, 0.7)' }}>CURRENT PASSWORD</label>
              <input 
                type="password" 
                required
                style={{ background: '#0a1f1f', border: '1px solid #1b3a3a', color: '#fff', padding: '8px', outline: 'none' }}
                value={formData.currentPassword}
                onChange={(e) => setFormData({...formData, currentPassword: e.target.value})}
              />
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <label style={{ fontSize: '10px', color: 'rgba(80, 227, 194, 0.7)' }}>NEW {type}</label>
              <input 
                type={isEmail ? "email" : "password"} 
                required
                style={{ background: '#0a1f1f', border: '1px solid #1b3a3a', color: '#fff', padding: '8px', outline: 'none' }}
                value={formData.primaryValue}
                onChange={(e) => setFormData({...formData, primaryValue: e.target.value})}
              />
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <label style={{ fontSize: '10px', color: 'rgba(80, 227, 194, 0.7)' }}>CONFIRM NEW {type}</label>
              <input 
                type={isEmail ? "email" : "password"} 
                required
                style={{ background: '#0a1f1f', border: '1px solid #1b3a3a', color: '#fff', padding: '8px', outline: 'none' }}
                value={formData.confirmValue}
                onChange={(e) => setFormData({...formData, confirmValue: e.target.value})}
              />
            </div>

            {status.error && <div className="rate-negative" style={{ fontSize: '10px', textAlign: 'center' }}>{status.error}</div>}
            {status.success && <div className="rate-positive" style={{ fontSize: '10px', textAlign: 'center' }}>{status.success}</div>}
          </form>
        </div>

        <div className="modal-actions">
          <button type="button" className="settings-button confirm" onClick={onClose} disabled={status.loading}>
            CANCEL
          </button>
          <button type="submit" onClick={handleSubmit} className="settings-button" disabled={status.loading}>
            {status.loading ? "PROCESSING..." : "CONFIRM"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default SettingsModal;